import { useEffect, useMemo, useState } from 'react';
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Text,
  Icon,
  Link,
  Tooltip,
} from '@chakra-ui/react';
import Select from 'react-select';
import EditorContainer from './EditorContainer';
import { Mail, Inmail, ConnectionRequest } from '@/assets/icons/outreach';
import { useDelay } from './delay';
import { GradientTag } from '@/components/ai/GradientTag';
import { Maximize } from '@/assets/icons/outreach';
import {
  getIsActionRequired,
  getIsCompleted,
  getIsSkipped,
  getIsScheduleEnabled,
} from './utils';
import { KSelectStylesConfig } from '@/ui-library/design-system/constants/KSelect.styles';
import { KText } from '@/ui-library/components/text';

export const StepAccordion = ({
  step,
  steps,
  index,
  moveNextStep,
  handleAccordion,
  activeIndex,
  flow,
  contactId,
}) => {
  const { ai_messages, step_interval, channel, step_candidate } = step || {};
  const [selectedOption, setSelectedOption] = useState(null);
  const [isScheduleEdited, setScheduleEdited] = useState(false);
  const { stage } = step_candidate || {};

  const iconObj = useMemo(
    () => ({
      email: Mail,
      li_inmail: Inmail,
      li_connection: ConnectionRequest,
    }),
    []
  );

  const stepDelay = useDelay({
    outreach: { steps },
    step,
  });

  const getIntervalValue = () => {
    const stepInterval = step_candidate?.custom_schedule || step_interval;
    const interval = stepDelay.find((delay) => delay.value === stepInterval);
    setSelectedOption(interval);
  };

  useEffect(() => {
    getIntervalValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step_interval, stepDelay]);

  const currentTab = activeIndex === index;

  const newStep = {
    ...step,
    template: {
      ...step.template,
      body: step.template?.body?.replace(
        '{{candidate.personalisation}}',
        '<ai-personalized-block></ai-personalized-block>'
      ),
    },
  };

  const renderSelect = () => {
    const isDisabled = stage && !getIsScheduleEnabled(stage);
    const handleSelectChange = (selectedOption) => {
      setSelectedOption(selectedOption);
      setScheduleEdited(true);
    };

    return (
      <Flex justifyContent="flex-end">
        <Tooltip
          label="Schedule modification is unavailable for this step."
          arrowSize={8}
          isDisabled={!isDisabled}
          hasArrow
          placement="top"
          borderRadius="4px"
        >
          <Box cursor="pointer">
            <Select
              value={selectedOption}
              onChange={handleSelectChange}
              isDisabled={isDisabled}
              styles={{
                ...KSelectStylesConfig({}),
                control: (provided) => ({
                  ...provided,
                  width: '150px',
                  fontSize: '14px !important',
                }),
              }}
              components={{
                IndicatorSeparator: () => null,
              }}
              options={stepDelay}
              isSearchable={false}
            />
          </Box>
        </Tooltip>
      </Flex>
    );
  };

  return (
    <AccordionItem
      key={step?.id}
      borderRadius={currentTab ? '0' : '12px'}
      my="8px"
      border="1px solid #EAECF0"
      bg="#FFF"
      boxShadow="0px 1px 2px 0px #1018280f, 0px 1px 3px 0px #1018281a"
      onClick={(e) => handleAccordion(e, index)}
      isDisabled={
        getIsActionRequired(stage) ||
        getIsCompleted(stage) ||
        getIsSkipped(stage)
      }
    >
      {({ isExpanded }) => (
        <>
          <AccordionButton
            _focus={{ border: 'none', outline: 0 }}
            _hover={{ bg: 'none' }}
            _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
            justifyContent="space-between"
          >
            <Flex flex="1" textAlign="left" alignItems="center" gap="8px">
              <Icon as={iconObj[channel]} width="40px" height="40px" />
              <KText>{step?.name}</KText>
            </Flex>
            <Flex flex="1" justifyContent="flex-end">
              {currentTab ? (
                renderSelect()
              ) : (
                <Flex justifyContent="flex-end" gap="20px" alignItems="center">
                  <Box flex="1">
                    {getIsCompleted(stage) && (
                      <KText
                        bg="pastel-2-light"
                        color="pastel-2-dark"
                        padding="0px 10px"
                        borderRadius="16px"
                      >
                        Completed
                      </KText>
                    )}
                    {getIsSkipped(stage) && (
                      <KText
                        bg="pastel-1-light"
                        color="pastel-1-dark"
                        padding="0px 10px"
                        borderRadius="16px"
                      >
                        Skipped
                      </KText>
                    )}
                    {getIsActionRequired(stage) && (
                      <Link
                        href={`/flows/${flow?.id}/candidates?errors=all`}
                        target="_blank"
                        _hover={{ textDecoration: 'none' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Flex
                          alignItems="center"
                          bg="pastel-1-light"
                          padding="0px 10px"
                          borderRadius="16px"
                        >
                          <KText
                            color="pastel-1-dark"
                            borderRadius="16px"
                            whiteSpace="nowrap"
                          >
                            Action Required
                          </KText>
                          <Icon
                            as={Maximize}
                            width="15px"
                            height="15px"
                            color="#E31B54"
                            ml="5px"
                          />
                        </Flex>
                      </Link>
                    )}
                  </Box>
                  <Flex
                    gap="10px"
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    {step.mode === 'auto' ? (
                      <Text>Automatic</Text>
                    ) : (
                      <Text>Manual</Text>
                    )}
                    {step?.personalisation && <GradientTag>AI</GradientTag>}
                  </Flex>
                </Flex>
              )}
            </Flex>
          </AccordionButton>
          <AccordionPanel>
            <EditorContainer
              moveNextStep={moveNextStep}
              step={newStep}
              aiMessages={ai_messages}
              index={index}
              scheduleDelay={selectedOption}
              flow={flow}
              contactId={contactId}
              isScheduleEdited={isScheduleEdited}
              isExpanded={isExpanded}
            />
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};
